/**
 * Manabua statistics
 */
import { detect } from 'detect-browser';
import { v4 as uuid } from 'uuid';
import type { UserIdentity, VideoSliceEndReason, VideoSlicePlayed } from './types';
import type { ActivityStreamEvent } from './internal-types';

type DetectedBrowser = ReturnType<typeof detect>;

interface LanguageSelection {
  audio: string;
  subtitle: string;
}

interface ContentSessionStartedPayload {
  sessionId: string;
  timestamp: string;
  contentReference: {
    type: 'Video';
    id: string;
    categoryId?: string;
    areaId?: string;
  };
  userIdentity: UserIdentity;
  deviceInfo: {
    type: 'Browser';
    name: string;
    version: string;
  };
  appInfo: {
    type: 'Web';
    version: string;
    language: string;
  };
  service: string;
}

interface VideoSlicePlayedPayload {
  sessionId: string;
  timestamp: string;
  startSeconds: number;
  endSeconds: number;
  endReason: VideoSliceEndReason;
  narrationLanguage: string;
  subtitlesLanguage?: string;
}

interface ContentSessionEndedPayload {
  sessionId: string;
  timestamp: string;
}

type StatisticsEvent =
  | ActivityStreamEvent<ContentSessionStartedPayload>
  | ActivityStreamEvent<VideoSlicePlayedPayload>
  | ActivityStreamEvent<ContentSessionEndedPayload>;

interface StatisticsState {
  application?: string;
  applicationVersion?: string;
  activityStreamEndpoint?: string;
  videoUid: string;
  categoryUid?: string;
  areaUid?: string;
  uuid: string;
  statistics: LanguageSelection;
  token?: string;
  getAccessToken?: () => Promise<string | undefined>;
  userIdentity: UserIdentity;
  browser: DetectedBrowser;
  siteLanguage?: string;
  sessionStarted: boolean;
  lastStartSeconds: number;
}

export default class Statistics {
  declare onVideoSlicePlayed?: (slice: VideoSlicePlayed) => void;

  declare state: StatisticsState;

  constructor(
    videoUid: string,
    categoryUid: string | undefined,
    areaUid: string | undefined,
    userIdentity: UserIdentity,
    activeAudioTrack: string,
    activityStreamEndpoint: string | undefined,
    token: string | undefined,
    getAccessToken: (() => Promise<string | undefined>) | undefined, // Providing a callback to be used to get a new access token, to avoid using a stale token
    application: string | undefined,
    applicationVersion: string | undefined,
    siteLanguage: string | undefined,
    onVideoSlicePlayed?: (slice: VideoSlicePlayed) => void,
  ) {
    this.onVideoSlicePlayed = onVideoSlicePlayed;
    this.state = {
      application,
      applicationVersion,
      activityStreamEndpoint,
      videoUid,
      categoryUid,
      areaUid,
      uuid: uuid(),
      // Initial statistic
      statistics: {
        audio: activeAudioTrack,
        subtitle: '',
      },
      token,
      getAccessToken,
      userIdentity,
      browser: detect(),
      siteLanguage,
      sessionStarted: false,
      lastStartSeconds: 0,
    };
  }

  changeLanguage(options: LanguageSelection) {
    this.state.statistics = {
      audio: options.audio,
      subtitle: options.subtitle,
    };
  }

  onStartPlaying(startSeconds: number) {
    this.state.lastStartSeconds = startSeconds;
  }

  onStopPlaying(endSeconds: number, endReason: VideoSliceEndReason) {
    if (endSeconds > this.state.lastStartSeconds) {
      this.videoSlicePlayed(this.state.lastStartSeconds, endSeconds, endReason);
    }
  }

  async sendEventsToActivityStream(events: StatisticsEvent[]) {
    const headers: Record<string, string> = {};

    let accessToken = this.state.token;
    if (this.state.getAccessToken) {
      accessToken = await this.state.getAccessToken();
    }

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    const mappedEvents = events.map((event) => ({
      partitionKey: this.state.uuid,
      // sourceApplication is always 'manabua' to keep all the statistics together
      sourceApplication: 'manabua',
      ...event,
    }));
    if (!this.state.activityStreamEndpoint) {
      console.log('activityStreamEndpoint is not set', { events: mappedEvents });
      return;
    }
    // Use fetch with "keepalive: true" to tell browser to send request even if the page is unloaded
    fetch(this.state.activityStreamEndpoint, {
      body: JSON.stringify(mappedEvents),
      keepalive: true,
      headers,
      method: 'POST',
    }).catch((error) => {
      console.error('Failed to send events to activity stream', error);
    });
  }

  sessionStarted() {
    if (this.state.sessionStarted) {
      return;
    }
    const newVideoStartedEvent: ActivityStreamEvent<ContentSessionStartedPayload> = {
      type: 'ContentSessionStartedEventV1',
      payload: {
        sessionId: this.state.uuid,
        timestamp: new Date().toISOString(),
        contentReference: {
          type: 'Video',
          id: this.state.videoUid,
          categoryId: this.state.categoryUid,
          areaId: this.state.areaUid,
        },
        userIdentity: this.state.userIdentity,
        deviceInfo: {
          type: 'Browser',
          name: this.state.browser?.name || 'unknown',
          version: this.state.browser?.version || '0.0.0',
        },
        appInfo: {
          type: 'Web',
          version: this.state.applicationVersion || '0',
          language: this.state.siteLanguage || 'sv',
        },
        service: this.state.application || 'external',
      },
    };
    this.sendEventsToActivityStream([newVideoStartedEvent]);
    this.state.sessionStarted = true;
  }

  createVideoSlicePlayedEvent(
    startSeconds: number,
    endSeconds: number,
    endReason: VideoSliceEndReason,
  ): ActivityStreamEvent<VideoSlicePlayedPayload> {
    const subtitlesLanguage = this.state.statistics.subtitle || undefined;
    return {
      type: 'VideoSlicePlayedV1',
      payload: {
        sessionId: this.state.uuid,
        timestamp: new Date().toISOString(),
        startSeconds,
        endSeconds,
        endReason,
        narrationLanguage: this.state.statistics.audio,
        subtitlesLanguage,
      },
    };
  }

  videoSlicePlayed(startSeconds: number, endSeconds: number, endReason: VideoSliceEndReason) {
    this.sendEventsToActivityStream([this.createVideoSlicePlayedEvent(startSeconds, endSeconds, endReason)]);
    this.notifyVideoSlicePlayed(startSeconds, endSeconds, endReason, false);
  }

  notifyVideoSlicePlayed(
    startSeconds: number,
    endSeconds: number,
    endReason: VideoSliceEndReason,
    sentWhenPageHidden: boolean,
  ) {
    if (!this.onVideoSlicePlayed) {
      return;
    }
    this.onVideoSlicePlayed({
      videoId: this.state.videoUid,
      videoSessionId: this.state.uuid,
      categoryId: this.state.categoryUid,
      areaId: this.state.areaUid,
      service: this.state.application || 'external',
      startSeconds,
      endSeconds,
      endReason,
      narrationLanguage: this.state.statistics.audio,
      subtitlesLanguage: this.state.statistics.subtitle,
      sentWhenPageHidden: Boolean(sentWhenPageHidden),
    });
  }

  sessionEnded(isPlaying: boolean, endSeconds: number) {
    if (this.state.sessionStarted) {
      const events: StatisticsEvent[] = [];
      if (isPlaying) {
        events.push(this.createVideoSlicePlayedEvent(this.state.lastStartSeconds, endSeconds, 'end'));
        this.notifyVideoSlicePlayed(this.state.lastStartSeconds, endSeconds, 'end', true);
      }
      const newContentSessionEndedEvent: ActivityStreamEvent<ContentSessionEndedPayload> = {
        type: 'ContentSessionEndedEventV1',
        payload: { sessionId: this.state.uuid, timestamp: new Date().toISOString() },
      };
      events.push(newContentSessionEndedEvent);
      this.sendEventsToActivityStream(events);
    }
  }
}
